import { useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  Clock,
  Download,
  ExternalLink,
  Eye,
  EyeOff,
  Film,
  Play,
  Search,
  User,
  Users,
} from 'lucide-react';

import { LessonPlayer } from '@/components/LessonPlayer';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { useApiQuery } from '@/hooks/useApiQuery';
import { cn, getInitials } from '@/lib/utils';
import type { RecordingWatcher } from '@/types/recording';
import { recordingsApi } from '../api/recordings.api';
import { SubjectThumbnailCard, getSubjectCategory } from '../components/recordings/SubjectThumbnailCard';
import { formatVideoLength } from '@/lib/format';
import { toVideoEmbed } from '../components/recordings/videoEmbed';
import { lessonPath, returnToOf } from '../components/recordings/lessonLink';
import { formatInstituteDateTime } from '../utils';

type Filter = 'ALL' | 'WATCHED' | 'NOT_WATCHED';

const FILTERS: { value: Filter; label: string }[] = [
  { value: 'ALL', label: 'Everyone' },
  { value: 'NOT_WATCHED', label: 'Not watched yet' },
  { value: 'WATCHED', label: 'Watched' },
];

export function TeacherRecordedClassPage() {
  const { recordingId } = useParams<{ recordingId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // The library view this lesson was opened from — its class, filters and page.
  const returnTo = returnToOf(location.state);

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('ALL');
  const [isWatchersModalOpen, setIsWatchersModalOpen] = useState(false);
  const [isDescExpanded, setIsDescExpanded] = useState(false);

  const { data, isLoading, error, refetch } = useApiQuery(
    () => recordingsApi.detail(recordingId ?? ''),
    [recordingId],
  );

  const { data: libraryData, isLoading: isLibraryLoading } = useApiQuery(
    () =>
      data?.recording.classSectionUuid
        ? recordingsApi.list(data.recording.classSectionUuid)
        : Promise.resolve(null),
    [data?.recording.classSectionUuid],
  );

  const students = data?.students ?? [];
  const otherRecordings = libraryData?.recordings ?? [];

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();

    return students.filter((student) => {
      const hasWatched = student.watchedAt !== null;
      if (filter === 'WATCHED' && !hasWatched) return false;
      if (filter === 'NOT_WATCHED' && hasWatched) return false;
      if (!needle) return true;

      return (
        student.fullName.toLowerCase().includes(needle) ||
        (student.rollNumber ?? '').toLowerCase().includes(needle) ||
        student.admissionNumber.toLowerCase().includes(needle)
      );
    });
  }, [students, search, filter]);

  // Only to name the host on the "open it there" button. The player works the
  // link out for itself, so a lesson we host needs none of this.
  const embed = data?.recording.videoUrl ? toVideoEmbed(data.recording.videoUrl) : null;

  return (
    <div className="flex flex-col gap-3 max-w-[1600px] mx-auto w-full">
      {/* Top Navigation Bar: Back button and Class context */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button
          type="button"
          onClick={() => navigate(returnTo)}
          className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-pill text-xs font-bold text-ink bg-surface border border-line hover:bg-surface-2 hover:border-accent/40 shadow-xs transition-all cursor-pointer group active:scale-98"
        >
          <ArrowLeft className="h-4 w-4 text-accent group-hover:-translate-x-0.5 transition-transform" />
          <span>Back to Recorded Classes</span>
        </button>
        {data?.recording && (
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-ink-3">Active Class:</span>
            <span className="text-xs font-extrabold text-ink bg-surface-2 px-2.5 py-1 rounded-pill border border-line">
              {data.recording.displayName}
            </span>
          </div>
        )}
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8 card p-5">
            <SkeletonLines count={10} />
          </div>
          <div className="lg:col-span-4 card p-5">
            <SkeletonLines count={6} />
          </div>
        </div>
      )}

      {/* Error state */}
      {!isLoading && error && (
        <div className="card flex flex-col items-start gap-3 p-5">
          <Alert tone="critical" title="Could not load this lesson">
            {error.message}
          </Alert>
          <Button variant="secondary" size="sm" onClick={refetch}>
            Try again
          </Button>
        </div>
      )}

      {/* Main Content Layout */}
      {!isLoading && !error && data && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
          {/* Left Column: Video Player & Details (8 cols) */}
          <div className="lg:col-span-8 flex flex-col gap-5">
            <div className="card bg-surface border border-line rounded-[20px] p-4 shadow-2xs flex flex-col gap-4">
              {/* Video Player */}
              <div className="overflow-hidden rounded-[16px] bg-black shadow-md">
                <LessonPlayer
                  title={data.recording.title}
                  videoSource={data.recording.videoSource}
                  videoUrl={data.recording.videoUrl}
                  playbackUrl={data.recording.playbackUrl}
                />
              </div>

              {/* Video Info Section */}
              <div className="flex flex-col gap-3 pt-1">
                {/* Meta Badges Row & Download Action */}
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`inline-flex items-center px-2.5 py-1 rounded-[8px] text-xs font-extrabold uppercase tracking-wider ${getSubjectCategory(data.recording.subject.name).badge}`}
                    >
                      {data.recording.subject.name}
                    </span>
                    {data.recording.topic && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-[8px] text-xs font-bold text-ink-2 bg-surface-2 border border-line">
                        {data.recording.topic}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-ink-3">
                      <Calendar className="h-3.5 w-3.5 text-ink-3" />
                      Posted {formatInstituteDateTime(data.recording.postedAt)}
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-ink-3">
                      <User className="h-3.5 w-3.5 text-ink-3" />
                      By {data.recording.postedBy}
                    </span>
                    {data.recording.durationSeconds !== null && (
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-ink-3">
                        <Clock className="h-3.5 w-3.5 text-ink-3" />
                        {formatVideoLength(data.recording.durationSeconds)}
                      </span>
                    )}
                  </div>

                  {/* Action Buttons: Light Color Download or External Provider */}
                  <div className="flex items-center gap-2">
                    {data.recording.videoSource === 'UPLOAD'
                      ? data.recording.downloadUrl && (
                          <button
                            type="button"
                            onClick={() => window.open(data.recording.downloadUrl ?? '', '_blank')}
                            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-[10px] text-xs font-bold bg-blue-500/15 hover:bg-blue-500/25 text-blue-600 dark:text-blue-400 border border-blue-500/30 transition-all cursor-pointer shadow-2xs active:scale-98"
                          >
                            <Download className="h-3.5 w-3.5" />
                            <span>Download Video</span>
                          </button>
                        )
                      : embed &&
                        data.recording.videoUrl && (
                          <button
                            type="button"
                            onClick={() =>
                              window.open(
                                data.recording.videoUrl ?? '',
                                '_blank',
                                'noopener,noreferrer',
                              )
                            }
                            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-[10px] text-xs font-bold bg-blue-500/15 hover:bg-blue-500/25 text-blue-600 dark:text-blue-400 border border-blue-500/30 transition-all cursor-pointer shadow-2xs active:scale-98"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                            <span>Open on {embed.provider}</span>
                          </button>
                        )}
                  </div>
                </div>

                {/* Lesson Title */}
                <h1 className="text-xl sm:text-2xl font-black text-ink tracking-tight">
                  {data.recording.title}
                </h1>

                {/* Lesson Description (Directly below Title with 3-line clamp & ellipsis) */}
                {data.recording.description && (
                  <div className="flex flex-col gap-1">
                    <p
                      className={cn(
                        'whitespace-pre-line text-sm text-ink-2 font-medium leading-relaxed',
                        !isDescExpanded && 'line-clamp-3',
                      )}
                    >
                      <span className="text-xs font-bold text-ink-3 uppercase tracking-wider mr-2 select-none">
                        Lesson Description:
                      </span>
                      {data.recording.description}
                    </p>
                    {data.recording.description.length > 180 && (
                      <button
                        type="button"
                        onClick={() => setIsDescExpanded((prev) => !prev)}
                        className="text-xs font-bold text-accent hover:underline self-start cursor-pointer mt-0.5"
                      >
                        {isDescExpanded ? 'Show less' : 'Read more'}
                      </button>
                    )}
                  </div>
                )}

                {/* Single Interactive Views / Student Watchers Trigger Bar */}
                <div className="flex items-center justify-between gap-3 pt-3 border-t border-line mt-1">
                  <button
                    type="button"
                    onClick={() => setIsWatchersModalOpen(true)}
                    className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-bold bg-accent/10 text-accent border border-accent/25 hover:bg-accent/20 hover:border-accent/40 shadow-2xs transition-all cursor-pointer group"
                  >
                    <Eye className="h-4 w-4 group-hover:scale-110 transition-transform" />
                    <span>
                      <strong className="font-extrabold">{data.summary.watched}</strong> /{' '}
                      {data.summary.total} watched
                    </span>
                    <span className="text-2xs font-semibold text-accent/80 ml-1 underline underline-offset-2">
                      View student list &rarr;
                    </span>
                  </button>

                  <span className="text-xs text-ink-3 font-mono">
                    {/* "All watched" only when there is somebody to have watched
                        it — an empty roster has nobody, not everybody. */}
                    {data.summary.total === 0
                      ? 'No students in this class yet'
                      : data.summary.notWatched > 0
                        ? `${data.summary.notWatched} pending`
                        : 'All students watched'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Other Posted Videos Playlist (4 cols) */}
          <div className="lg:col-span-4 flex flex-col gap-4">
            <div className="card bg-surface border border-line rounded-[20px] p-4 shadow-2xs flex flex-col gap-3">
              <div className="flex items-center justify-between pb-2 border-b border-line">
                <div className="flex items-center gap-2">
                  <Film className="h-4.5 w-4.5 text-accent" />
                  <h3 className="text-sm font-bold text-ink">Other Posted Lessons</h3>
                </div>
                {otherRecordings.length > 0 && (
                  <span className="text-xs font-mono font-bold text-ink-3 bg-surface-2 px-2 py-0.5 rounded-pill border border-line">
                    {otherRecordings.length} total
                  </span>
                )}
              </div>

              {/* Playlist Item Cards */}
              <div className="flex flex-col gap-2.5 max-h-[calc(100vh-220px)] overflow-y-auto pr-1">
                {isLibraryLoading ? (
                  <SkeletonLines count={5} />
                ) : otherRecordings.length === 0 ? (
                  <p className="text-center py-8 text-xs text-ink-3">
                    No other recorded lessons in this class.
                  </p>
                ) : (
                  otherRecordings.map((rec) => {
                    const isCurrent = rec.uuid === recordingId;
                    return (
                      <div
                        key={rec.uuid}
                        onClick={() => {
                          // Passed on, so Back still returns to the library view, not the last lesson.
                          if (!isCurrent) navigate(lessonPath(rec.uuid), { state: location.state });
                        }}
                        className={cn(
                          'p-2 rounded-[14px] border transition-all flex items-center gap-2.5',
                          isCurrent
                            ? 'border-accent bg-accent/10 shadow-xs cursor-default ring-1 ring-accent/30'
                            : 'border-line bg-surface-2/40 hover:bg-surface-2 hover:border-accent/30 cursor-pointer',
                        )}
                      >
                        <div className="shrink-0">
                          <SubjectThumbnailCard recording={rec} />
                        </div>
                        <div className="min-w-0 flex-1 flex flex-col justify-center">
                          {isCurrent && (
                            <span className="text-[10px] font-black text-accent uppercase tracking-wider mb-0.5 flex items-center gap-1">
                              <Play className="h-2.5 w-2.5 fill-accent" /> Now Playing
                            </span>
                          )}
                          <h4 className="text-xs font-bold text-ink line-clamp-2 leading-tight">
                            {rec.title}
                          </h4>
                          <div className="flex items-center gap-1.5 text-[11px] text-ink-3 mt-1 flex-wrap">
                            {rec.topic && <span className="truncate">{rec.topic}</span>}
                            {rec.topic && <span>•</span>}
                            <span className="inline-flex items-center gap-0.5 font-mono text-ink-2">
                              <Eye className="h-3 w-3 text-ink-3" />
                              {rec.viewCount ?? 0}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Watchers History Modal */}
      {data && (
        <Modal
          isOpen={isWatchersModalOpen}
          onClose={() => setIsWatchersModalOpen(false)}
          title="Student Watch History"
          description={`Roster and viewing activity for "${data.recording.title}"`}
          size="lg"
        >
          <div className="flex flex-col gap-4">
            {/* Summary Badges */}
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone="success">{data.summary.watched} watched</Badge>
              {data.summary.notWatched > 0 && (
                <Badge tone="warning">{data.summary.notWatched} not yet</Badge>
              )}
            </div>

            {/* Search & Filter Bar */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="flex-1">
                <Input
                  label="Search"
                  hideLabel
                  leftIcon={<Search className="h-4 w-4" />}
                  placeholder="Search by name, roll or admission number"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </div>
              <Select
                label="Show watched or not watched"
                hideLabel
                className="sm:w-48 py-2"
                options={FILTERS.map((item) => ({ value: item.value, label: item.label }))}
                value={filter}
                onChange={(event) => setFilter(event.target.value as Filter)}
              />
            </div>

            {/* Student List */}
            {visible.length === 0 ? (
              <div className="py-8">
                <EmptyState
                  icon={<Users className="h-5 w-5" />}
                  title="Nobody matches"
                  description="Clear the search or the filter to see the whole class again."
                />
              </div>
            ) : (
              <ul className="flex flex-col divide-y divide-line rounded-[14px] border border-line bg-surface overflow-hidden">
                {visible.map((student) => (
                  <li key={student.uuid}>
                    <WatcherRow student={student} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

/**
 * One child's line: whether they have watched, and when they first did.
 */
function WatcherRow({ student }: { student: RecordingWatcher }) {
  const hasWatched = student.watchedAt !== null;

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-3 px-4 py-3',
        hasWatched ? 'bg-ok-bg/30' : 'bg-surface',
      )}
    >
      <div className="flex min-w-0 items-center gap-3">
        <span
          className={cn(
            'grid h-8 w-8 shrink-0 place-items-center rounded-full text-xs font-[650]',
            hasWatched ? 'bg-ok/15 text-ok' : 'bg-surface-2 text-ink-3',
          )}
          aria-hidden
        >
          {getInitials(
            student.fullName.split(' ')[0] ?? '',
            student.fullName.split(' ').at(-1) ?? '',
          )}
        </span>
        <div className="min-w-0">
          <p className="truncate text-sm font-[560] text-ink">{student.fullName}</p>
          <p className="truncate font-mono text-xs text-ink-3">
            {student.rollNumber ? `Roll ${student.rollNumber} · ` : ''}
            {student.admissionNumber}
          </p>
        </div>
      </div>

      <div className="flex shrink-0 flex-col items-end gap-1">
        <span
          className={cn(
            'flex items-center gap-1.5 text-xs font-[560]',
            hasWatched ? 'text-green-600' : 'text-orange-500',
          )}
        >
          {hasWatched ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
          {hasWatched ? 'Watched' : 'Not yet'}
        </span>
        {student.watchedAt && (
          <span className="text-2xs text-ink-3">{formatInstituteDateTime(student.watchedAt)}</span>
        )}
      </div>
    </div>
  );
}
